import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";

const POLL_INTERVAL_MS = 30_000;

function formatTimestamp(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const date = d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  const time = d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  return `${date} ${time}`;
}

function getNicknameFromEmail(username, whitelistEntries) {
  if (!username || !username.includes("@")) return username;
  const entry = whitelistEntries.find((e) => e.email === username);
  if (entry?.nickname && entry.nickname !== "") return entry.nickname;
  return username;
}

export default function RecentClaimsFeed({ whitelistEntries = [] }) {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadClaims = useCallback(async () => {
    try {
      const { data } = await api.get("/card-holders/recent");
      // Backend returns oldest-first; reverse for display (most recent at top)
      // but keep the original order as-is per spec (oldest-first feed)
      setClaims(Array.isArray(data) ? data : []);
      setError(null);
    } catch (e) {
      setError("Unable to load recent claims.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadClaims();
    const id = setInterval(loadClaims, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [loadClaims]);

  // Hide entirely when there are no claims and we're not in a loading/error state
  if (!loading && !error && claims.length === 0) {
    return null;
  }

  return (
    <div
      className="mb-4 border border-slate-800 bg-[#121822]/60 px-5 py-4"
      data-testid="recent-claims-feed"
    >
      <div className="text-[10px] tracking-[0.3em] uppercase text-slate-500 mb-3 text-center">
        Recent Claims
      </div>

      {loading && (
        <div
          className="text-orange-400 text-xs tracking-widest uppercase animate-pulse text-center"
          data-testid="recent-claims-loading"
        >
          Loading…
        </div>
      )}

      {!loading && error && (
        <div
          className="text-slate-600 text-xs text-center"
          data-testid="recent-claims-error"
        >
          {error}
        </div>
      )}

      {!loading && !error && claims.length > 0 && (
        <div
          className="space-y-1.5 text-center"
          data-testid="recent-claims-list"
        >
          {claims.map((claim) => (
            <div
              key={claim.id}
              className="text-xs text-slate-400 font-mono"
              data-testid="recent-claim-item"
            >
              <span className="text-slate-200 font-medium">{getNicknameFromEmail(claim.username, whitelistEntries)}</span>
              <span className="text-slate-600 mx-1.5">—</span>
              <span className="text-slate-300">{claim.location}</span>
              <span className="text-slate-600 mx-1.5">—</span>
              <span className="text-slate-300">{claim.cardName}</span>
              <span className="text-slate-600 mx-1.5">—</span>
              <span className="text-slate-500">{formatTimestamp(claim.claimedAt)}</span>
              {claim.proxyClaimedBy && (
                <>
                  <span className="text-slate-600 mx-1.5">·</span>
                  <span className="text-slate-500">
                    proxy:{" "}
                    <span className="text-slate-400">{claim.proxyClaimedBy}</span>
                  </span>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
